// DemoModeTab.js
// Demo Mode tab with Multi-Brain AI Processing
// Extracted from AIWorkforcePage.js for modularity

import React from 'react';
import { TabsContent } from '../../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import ReactMarkdown from 'react-markdown';
import {
  Bot, Target, Cpu, Activity, Shield, FileText, TrendingUp, Search, Zap, Sparkles,
  Play, Save, Brain, ChevronDown, CheckCircle, XCircle, Copy, ListTodo, ImageIcon,
  Mail, Maximize2
} from 'lucide-react';

const DEMO_TASK_TYPES = [
  { id: 'lead_qualification', name: 'Lead Qualification', description: 'Qualify and score incoming leads', icon: 'Target', category: 'sales' },
  { id: 'data_enrichment', name: 'Data Enrichment', description: 'Enrich contact data with insights', icon: 'Database', category: 'data' },
  { id: 'support_triage', name: 'Support Ticket Triage', description: 'Categorize and route support tickets', icon: 'Headphones', category: 'support' },
  { id: 'campaign_review', name: 'Campaign Compliance Review', description: 'Review marketing campaigns', icon: 'Shield', category: 'marketing' },
  { id: 'content_generation', name: 'Content Generation', description: 'Generate marketing copy or emails', icon: 'FileText', category: 'marketing' },
  { id: 'customer_analysis', name: 'Customer Analysis', description: 'Analyze customer behavior patterns', icon: 'TrendingUp', category: 'analytics' },
  { id: 'deal_strategy', name: 'Deal Strategy', description: 'Suggest deal closing strategies', icon: 'Target', category: 'sales' },
  { id: 'market_research', name: 'Market Research', description: 'Research market trends and competitors', icon: 'Search', category: 'research' },
  { id: 'retention_analysis', name: 'Retention Analysis', description: 'Analyze and improve retention', icon: 'Activity', category: 'success' },
  { id: 'custom_task', name: 'Custom Task', description: 'Define your own task scenario', icon: 'Zap', category: 'custom' }
];

const TaskIcon = ({ iconName }) => {
  const iconMap = {
    'Target': Target,
    'Database': Cpu,
    'Headphones': Activity,
    'Shield': Shield,
    'FileText': FileText,
    'TrendingUp': TrendingUp,
    'Search': Search,
    'Activity': Activity,
    'Zap': Zap
  };
  const Icon = iconMap[iconName] || Zap;
  return <Icon className="h-4 w-4" />;
};

const DemoModeTab = ({
  // Task selection state
  selectedDemoTask,
  onSelectDemoTask,
  // Agent selection
  selectedDemoAgent,
  onSelectDemoAgent,
  agents,
  // Campaign linking
  selectedCampaignId,
  onSelectCampaign,
  campaigns,
  onLoadCampaigns,
  // Custom input
  demoCustomInput,
  onCustomInputChange,
  // Streaming mode
  streamingMode,
  onStreamingModeChange,
  // Execution state
  demoRunning,
  streamingPhase,
  streamingAgent,
  // Brain streams
  leftBrainStream,
  rightBrainStream,
  brainStemStream,
  // Expanded brain state
  expandedBrain,
  onExpandedBrainChange,
  // Demo completion state
  demoCompleted,
  // Result state
  demoResult,
  expandedResultBrain,
  onExpandedResultBrainChange,
  // Created tasks and deliverables
  createdTasks,
  executingTasks,
  deliverables,
  // Quick runs
  quickRuns,
  onShowSaveQuickRunDialog,
  onLoadQuickRun,
  // Actions
  onRunDemo,
  onCopyResults,
  toast
}) => {
  return (
    <TabsContent value="demo" className="space-y-6">
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-amber-900">Demo Mode - Multi-Brain AI Processing</h2>
            <p className="text-amber-700 mt-1">
              Experience the AI Virtual Workforce in action. Watch as <strong>Left Brain (GPT-4o)</strong> provides analytical insights, 
              <strong> Right Brain (Claude)</strong> adds creative perspectives, and <strong>Cortex (Gemini)</strong> combines both into a unified response.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Task Selection Panel */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Select Task Type
            </CardTitle>
            <CardDescription>Choose a scenario to demonstrate</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {DEMO_TASK_TYPES.map(task => (
              <div
                key={task.id}
                className={`p-3 border rounded-lg cursor-pointer transition-all ${
                  selectedDemoTask === task.id 
                    ? 'border-amber-400 bg-amber-50 shadow-md' 
                    : 'hover:border-slate-300 hover:bg-slate-50'
                }`}
                onClick={() => onSelectDemoTask(task.id)}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                    selectedDemoTask === task.id ? 'bg-amber-200' : 'bg-slate-100'
                  }`}>
                    <TaskIcon iconName={task.icon} />
                  </div>
                  <span className="font-medium">{task.name}</span>
                </div>
                <p className="text-xs text-muted-foreground">{task.description}</p>
                <Badge variant="outline" className="text-xs mt-1">{task.category}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Execution Panel */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Run Demo Task
            </CardTitle>
            <CardDescription>
              {selectedDemoTask 
                ? `Ready to demonstrate: ${selectedDemoTask.replace('_', ' ')}`
                : 'Select a task type to begin'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Agent Selection */}
            <div>
              <Label>Select AI Agent</Label>
              <Select value={selectedDemoAgent || 'auto'} onValueChange={(v) => onSelectDemoAgent(v === 'auto' ? '' : v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Auto-select based on task type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-select (recommended)</SelectItem>
                  {agents.filter(a => a.status === 'active').map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex items-center gap-2">
                        <Bot className="h-4 w-4 text-violet-500" />
                        <span>{agent.name}</span>
                        <span className="text-xs text-muted-foreground">- {agent.role_title}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Choose which AI agent should process this task, or let the system auto-select.
              </p>
            </div>

            {/* Campaign Link (Optional) */}
            <div>
              <Label>Link to Campaign (Optional)</Label>
              <Select 
                value={selectedCampaignId || 'none'} 
                onValueChange={(v) => onSelectCampaign(v === 'none' ? '' : v)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="No campaign - create standalone project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No campaign - create standalone project</SelectItem>
                  {campaigns.map(campaign => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-blue-500" />
                        <span>{campaign.name}</span>
                        <Badge variant="outline" className="text-xs ml-2">{campaign.status}</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Link AI work to a campaign to keep all outputs organized under that campaign.
              </p>
              {campaigns.length === 0 && (
                <Button 
                  variant="link" 
                  size="sm" 
                  className="p-0 h-auto text-xs"
                  onClick={onLoadCampaigns}
                >
                  Load campaigns
                </Button>
              )}
            </div>

            {/* Custom Input */}
            <div>
              <Label>Custom Input / Context</Label>
              <Textarea
                placeholder={selectedDemoTask === 'custom_task' 
                  ? "Describe your custom task scenario in detail..."
                  : "Add custom context for the demo (e.g., specific lead details, ticket message, campaign brief, etc.)"
                }
                value={demoCustomInput}
                onChange={(e) => onCustomInputChange(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>

            {/* Streaming Mode Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="streaming-mode"
                  checked={streamingMode}
                  onChange={(e) => onStreamingModeChange(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="streaming-mode" className="text-sm cursor-pointer">
                  Real-time Streaming Mode
                </Label>
              </div>
              <Badge variant="outline" className={streamingMode ? 'bg-emerald-50 text-emerald-700' : ''}>
                {streamingMode ? 'Live Updates' : 'Standard'}
              </Badge>
            </div>

            {/* Run Button */}
            <div className="flex gap-2">
              <Button 
                onClick={onRunDemo} 
                disabled={!selectedDemoTask || demoRunning}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                data-testid="run-demo-btn"
              >
                {demoRunning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    {streamingPhase === 'task_creation' ? 'Creating tasks...' :
                     streamingPhase === 'task_execution' ? 'Executing tasks...' :
                     streamingPhase ? `Processing ${streamingPhase.replace('_', ' ')}...` : 'Starting...'}
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    {streamingMode ? 'Execute & Create Tasks' : 'Run Demo Task'}
                  </>
                )}
              </Button>
              
              {/* Save as Quick Run */}
              {demoCustomInput && (
                <Button
                  variant="outline"
                  onClick={onShowSaveQuickRunDialog}
                  disabled={!demoCustomInput || demoRunning}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  <Save className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            {/* Quick Runs Section */}
            {quickRuns.length > 0 && (
              <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Zap className="h-4 w-4 text-amber-500" />
                    Quick Runs
                  </Label>
                  <Badge variant="outline">{quickRuns.length} saved</Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                  {quickRuns.slice(0, 5).map(qr => (
                    <Button
                      key={qr.id}
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => onLoadQuickRun(qr)}
                    >
                      <Play className="h-3 w-3 mr-1" />
                      {qr.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Streaming Output */}
            {demoRunning && streamingMode && (
              <StreamingOutput
                streamingAgent={streamingAgent}
                streamingPhase={streamingPhase}
                leftBrainStream={leftBrainStream}
                rightBrainStream={rightBrainStream}
                brainStemStream={brainStemStream}
                expandedBrain={expandedBrain}
                onExpandedBrainChange={onExpandedBrainChange}
                demoCompleted={demoCompleted}
              />
            )}

            {/* Results (non-streaming or after completion) */}
            {demoResult && !demoRunning && (
              <DemoResults
                demoResult={demoResult}
                expandedResultBrain={expandedResultBrain}
                onExpandedResultBrainChange={onExpandedResultBrainChange}
                createdTasks={createdTasks}
                executingTasks={executingTasks}
                deliverables={deliverables}
                onCopyResults={onCopyResults}
              />
            )}

            {/* How It Works */}
            {!demoResult && !demoRunning && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Brain className="h-4 w-4" />
                  How the Multi-Brain Architecture Works
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-blue-700">1</span>
                    </div>
                    <div>
                      <span className="font-medium text-blue-700">Left Brain</span>
                      <span className="text-muted-foreground"> — Analyzes data, applies logic, evaluates rules and constraints</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-purple-700">2</span>
                    </div>
                    <div>
                      <span className="font-medium text-purple-700">Right Brain</span>
                      <span className="text-muted-foreground"> — Generates creative solutions, considers context and nuance</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-emerald-700">3</span>
                    </div>
                    <div>
                      <span className="font-medium text-emerald-700">Brain Stem</span>
                      <span className="text-muted-foreground"> — Synthesizes both perspectives into actionable output</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </TabsContent>
  );
};

// Streaming Output Sub-component
const StreamingOutput = ({
  streamingAgent,
  streamingPhase,
  leftBrainStream,
  rightBrainStream,
  brainStemStream,
  expandedBrain,
  onExpandedBrainChange,
  demoCompleted
}) => (
  <div className="mt-4 space-y-4">
    {streamingAgent && (
      <div className="flex items-center gap-2 p-3 bg-violet-50 border border-violet-200 rounded-lg">
        <Avatar className="h-10 w-10 bg-gradient-to-br from-violet-500 to-purple-600">
          <AvatarFallback className="bg-transparent text-white font-bold">
            <Bot className="h-5 w-5" />
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="font-medium">{streamingAgent.name}</p>
          <p className="text-sm text-muted-foreground">{streamingAgent.role_title}</p>
        </div>
        {streamingAgent.ai_config && (
          <div className="ml-auto flex gap-2 text-xs">
            <Badge variant="outline" className="bg-blue-50">{streamingAgent.ai_config.left_brain}</Badge>
            <Badge variant="outline" className="bg-purple-50">{streamingAgent.ai_config.right_brain}</Badge>
            <Badge variant="outline" className="bg-emerald-50">{streamingAgent.ai_config.cortex}</Badge>
          </div>
        )}
        <Badge className="bg-violet-100 text-violet-700 flex items-center gap-1 animate-pulse">
          <Bot className="h-3 w-3" /> Working...
        </Badge>
      </div>
    )}

    {/* Live Brain Outputs */}
    <div className="space-y-3">
      {/* Left Brain Stream */}
      <BrainStreamCard
        brainType="left"
        title="Left Brain (Analytical)"
        model="GPT-4o"
        stream={leftBrainStream}
        streamingPhase={streamingPhase}
        expandedBrain={expandedBrain}
        onExpandedBrainChange={onExpandedBrainChange}
        demoCompleted={demoCompleted}
        colorClass="blue"
        Icon={Cpu}
      />

      {/* Right Brain Stream */}
      <BrainStreamCard
        brainType="right"
        title="Right Brain (Creative)"
        model="Claude Sonnet 4.5"
        stream={rightBrainStream}
        streamingPhase={streamingPhase}
        expandedBrain={expandedBrain}
        onExpandedBrainChange={onExpandedBrainChange}
        demoCompleted={demoCompleted}
        colorClass="purple"
        Icon={Sparkles}
        waitingFor={leftBrainStream}
      />

      {/* Cortex Stream */}
      <BrainStreamCard
        brainType="cortex"
        title="Cortex (Combined Synthesis)"
        model="Gemini 3 Pro"
        stream={brainStemStream}
        streamingPhase={streamingPhase}
        expandedBrain={expandedBrain}
        onExpandedBrainChange={onExpandedBrainChange}
        demoCompleted={demoCompleted}
        colorClass="emerald"
        Icon={Brain}
        waitingFor={rightBrainStream}
        isCortex
      />
    </div>
  </div>
);

// Brain Stream Card Sub-component
const BrainStreamCard = ({
  brainType,
  title,
  model,
  stream,
  streamingPhase,
  expandedBrain,
  onExpandedBrainChange,
  demoCompleted,
  colorClass,
  Icon,
  waitingFor,
  isCortex
}) => {
  const isActive = isCortex 
    ? (streamingPhase === 'cortex' || streamingPhase === 'brain_stem')
    : streamingPhase === `${brainType}_brain`;
  
  const isComplete = isCortex
    ? ((streamingPhase === 'completed' || demoCompleted) && stream)
    : (demoCompleted && stream);

  const isOpen = expandedBrain === brainType || isActive || demoCompleted || (stream && stream.length > 0);

  return (
    <Collapsible open={isOpen} defaultOpen={true}>
      <div className={`rounded-lg border transition-all ${
        isActive ? `bg-${colorClass}-100 border-${colorClass}-300 shadow-md` : 
        isComplete ? `bg-${colorClass}-50 border-${colorClass}-200` : `bg-${colorClass}-50 border-${colorClass}-100`
      }`}>
        <CollapsibleTrigger 
          className={`w-full p-3 flex items-center gap-2 hover:bg-${colorClass}-100/50 transition-colors`} 
          onClick={() => onExpandedBrainChange(expandedBrain === brainType ? null : brainType)}
        >
          <div className={`h-7 w-7 rounded-full flex items-center justify-center ${
            isActive ? `bg-${colorClass}-400 animate-pulse` : 
            isComplete ? `bg-${colorClass}-500` : `bg-${colorClass}-200`
          }`}>
            <Icon className={`h-4 w-4 ${isComplete ? 'text-white' : `text-${colorClass}-700`}`} />
          </div>
          <span className={`font-medium text-${colorClass}-700`}>{title}</span>
          <Badge variant="outline" className={`text-xs bg-${colorClass}-100 text-${colorClass}-600 border-${colorClass}-200`}>{model}</Badge>
          {isActive && (
            <span className={`text-xs text-${colorClass}-500 animate-pulse ml-auto mr-2`}>
              {isCortex ? 'Synthesizing L+R...' : 'Processing...'}
            </span>
          )}
          {stream && !isActive && (
            <Badge className={`ml-auto bg-${colorClass}-500 text-white text-xs`}>Complete</Badge>
          )}
          {stream && (
            <Badge variant="outline" className="text-xs">{stream.length} chars</Badge>
          )}
          <ChevronDown className={`h-4 w-4 text-${colorClass}-600 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </CollapsibleTrigger>
        <CollapsibleContent forceMount={stream && stream.length > 0}>
          <div className={`max-h-[400px] overflow-y-auto px-4 pb-4 scrollbar-thin scrollbar-thumb-${colorClass}-300 scrollbar-track-${colorClass}-100`}>
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown>{stream || (waitingFor ? 'Waiting...' : 'Processing...')}</ReactMarkdown>
            </div>
            {isActive && <span className={`animate-pulse text-${colorClass}-500`}>▌</span>}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

// Demo Results Sub-component
const DemoResults = ({
  demoResult,
  expandedResultBrain,
  onExpandedResultBrainChange,
  createdTasks,
  executingTasks,
  deliverables,
  onCopyResults
}) => (
  <div className={`mt-4 p-4 rounded-lg border ${
    demoResult.success 
      ? 'bg-emerald-50/50 border-emerald-200' 
      : 'bg-red-50 border-red-200'
  }`}>
    <div className="flex items-center gap-2 mb-3">
      {demoResult.success ? (
        <CheckCircle className="h-5 w-5 text-emerald-600" />
      ) : (
        <XCircle className="h-5 w-5 text-red-600" />
      )}
      <span className={`font-medium ${demoResult.success ? 'text-emerald-700' : 'text-red-700'}`}>
        {demoResult.success ? 'Task Completed Successfully' : 'Task Failed'}
      </span>
      {demoResult.success && (
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={onCopyResults}
        >
          <Copy className="h-4 w-4 mr-1" /> Copy
        </Button>
      )}
    </div>

    {demoResult.agent && (
      <div className="flex items-center gap-2 mb-4 p-2 bg-white rounded-lg border">
        <Avatar className="h-10 w-10 bg-gradient-to-br from-violet-500 to-purple-600">
          <AvatarFallback className="bg-transparent text-white font-bold">
            <Bot className="h-5 w-5" />
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="font-medium">{demoResult.agent.name}</p>
          <p className="text-sm text-muted-foreground">{demoResult.agent.role_title}</p>
        </div>
        <Badge className="ml-auto bg-violet-100 text-violet-700 flex items-center gap-1">
          <Bot className="h-3 w-3" /> AI Employee
        </Badge>
      </div>
    )}

    {/* Brain Outputs */}
    {demoResult.execution_result && (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-sm">Multi-Brain Processing Results:</h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onExpandedResultBrainChange({ left: true, right: true, cortex: true })}
          >
            <Maximize2 className="h-3 w-3 mr-1" /> Expand All
          </Button>
        </div>
        
        <div className="space-y-3">
          {/* Left Brain Result */}
          <ResultBrainCard
            brainType="left"
            title="Left Brain (Analytical)"
            model="GPT-4o"
            content={demoResult.execution_result.left_brain_analysis || demoResult.execution_result.left_brain_output}
            expanded={expandedResultBrain.left}
            onExpandChange={(open) => onExpandedResultBrainChange(prev => ({...prev, left: open}))}
            colorClass="blue"
            Icon={Cpu}
          />

          {/* Right Brain Result */}
          <ResultBrainCard
            brainType="right"
            title="Right Brain (Creative)"
            model="Claude Sonnet 4.5"
            content={demoResult.execution_result.right_brain_analysis || demoResult.execution_result.right_brain_output}
            expanded={expandedResultBrain.right}
            onExpandChange={(open) => onExpandedResultBrainChange(prev => ({...prev, right: open}))}
            colorClass="purple"
            Icon={Sparkles}
          />

          {/* Cortex Result */}
          <ResultBrainCard
            brainType="cortex"
            title="Cortex (Final Synthesis)"
            model="Gemini 3 Pro"
            content={demoResult.execution_result.output || demoResult.execution_result.final_output || demoResult.execution_result.brain_stem_output}
            expanded={expandedResultBrain.cortex}
            onExpandChange={(open) => onExpandedResultBrainChange(prev => ({...prev, cortex: open}))}
            colorClass="emerald"
            Icon={Brain}
            isPrimary
          />
        </div>
      </div>
    )}

    {demoResult.error && (
      <div className="mt-3 p-2 bg-red-100 rounded text-sm text-red-700">
        Error: {demoResult.error}
      </div>
    )}
    
    {/* Auto-Created Tasks */}
    {createdTasks.length > 0 && (
      <CreatedTasksSection tasks={createdTasks} executingTasks={executingTasks} />
    )}
    
    {/* Deliverables */}
    {deliverables.length > 0 && (
      <DeliverablesSection deliverables={deliverables} />
    )}
  </div>
);

// Result Brain Card Sub-component
const ResultBrainCard = ({ brainType, title, model, content, expanded, onExpandChange, colorClass, Icon, isPrimary }) => (
  <Collapsible open={expanded} onOpenChange={onExpandChange}>
    <div className={`bg-${colorClass}-50 border${isPrimary ? '-2' : ''} border-${colorClass}-${isPrimary ? '300' : '200'} rounded-lg overflow-hidden ${isPrimary ? 'shadow-sm' : ''}`}>
      <CollapsibleTrigger className={`w-full p-3 flex items-center gap-2 hover:bg-${colorClass}-100/50 transition-colors`}>
        <div className={`h-7 w-7 rounded-full bg-${colorClass}-${isPrimary ? '500' : '200'} flex items-center justify-center`}>
          <Icon className={`h-4 w-4 ${isPrimary ? 'text-white' : `text-${colorClass}-700`}`} />
        </div>
        <span className={`font-medium text-${colorClass}-700`}>{title}</span>
        <Badge variant={isPrimary ? 'default' : 'outline'} className={`text-xs ${isPrimary ? `bg-${colorClass}-500 text-white` : `bg-${colorClass}-100 text-${colorClass}-600`}`}>{model}</Badge>
        {isPrimary && <Badge className={`ml-2 bg-${colorClass}-100 text-${colorClass}-700`}>Primary Output</Badge>}
        <ChevronDown className={`h-4 w-4 text-${colorClass}-600 ml-auto transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="max-h-[400px] overflow-y-auto">
          <div className="p-4 pt-0 prose prose-sm max-w-none">
            <ReactMarkdown>
              {content || 'No output'}
            </ReactMarkdown>
          </div>
        </div>
      </CollapsibleContent>
    </div>
  </Collapsible>
);

// Created Tasks Section Sub-component
const CreatedTasksSection = ({ tasks, executingTasks }) => (
  <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
    <div className="flex items-center justify-between mb-3">
      <h4 className="font-medium text-indigo-900 flex items-center gap-2">
        <ListTodo className="h-4 w-4" />
        Execution Pathway Tasks ({tasks.length})
      </h4>
      <Badge variant="outline" className="bg-indigo-100 text-indigo-700">
        {executingTasks ? 'Executing...' : 'Auto-created'}
      </Badge>
    </div>
    <div className="space-y-2">
      {tasks.map((task, idx) => (
        <div 
          key={task.id} 
          className={`p-3 bg-white rounded-lg border flex items-center gap-3 ${
            task.status === 'completed' ? 'border-emerald-300' :
            task.status === 'executing' ? 'border-amber-300' :
            task.status === 'failed' ? 'border-red-300' :
            'border-slate-200'
          }`}
        >
          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
            task.status === 'completed' ? 'bg-emerald-100' :
            task.status === 'executing' ? 'bg-amber-100 animate-pulse' :
            task.status === 'failed' ? 'bg-red-100' :
            'bg-slate-100'
          }`}>
            {task.status === 'completed' ? <CheckCircle className="h-4 w-4 text-emerald-600" /> :
             task.status === 'executing' ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-500 border-t-transparent" /> :
             task.status === 'failed' ? <XCircle className="h-4 w-4 text-red-600" /> :
             <span className="text-xs font-bold">{idx + 1}</span>}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{task.title}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs">
                {task.type || 'task'}
              </Badge>
              {task.assigned_to && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Bot className="h-3 w-3" /> {task.assigned_to}
                </span>
              )}
            </div>
          </div>
          <Badge className={
            task.status === 'completed' ? 'bg-emerald-500' :
            task.status === 'executing' ? 'bg-amber-500' :
            task.status === 'failed' ? 'bg-red-500' :
            'bg-slate-500'
          }>
            {task.status || 'pending'}
          </Badge>
        </div>
      ))}
    </div>
  </div>
);

// Deliverables Section Sub-component
const DeliverablesSection = ({ deliverables }) => (
  <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg">
    <div className="flex items-center justify-between mb-3">
      <h4 className="font-medium text-teal-900 flex items-center gap-2">
        <FileText className="h-4 w-4" />
        Generated Deliverables ({deliverables.length})
      </h4>
    </div>
    <div className="grid gap-3">
      {deliverables.map((deliverable, idx) => (
        <div key={idx} className="p-3 bg-white rounded-lg border border-teal-200">
          <div className="flex items-center gap-2 mb-2">
            {deliverable.type === 'image' && <ImageIcon className="h-4 w-4 text-pink-500" />}
            {deliverable.type === 'document' && <FileText className="h-4 w-4 text-blue-500" />}
            {deliverable.type === 'analysis' && <TrendingUp className="h-4 w-4 text-purple-500" />}
            {deliverable.type === 'email' && <Mail className="h-4 w-4 text-amber-500" />}
            {!['image', 'document', 'analysis', 'email'].includes(deliverable.type) && <FileText className="h-4 w-4 text-slate-500" />}
            <span className="font-medium capitalize">{deliverable.type}</span>
            <Badge variant="outline" className="text-xs ml-auto">{deliverable.format}</Badge>
          </div>
          {deliverable.preview && (
            <div className="text-sm text-muted-foreground bg-slate-50 p-2 rounded border max-h-32 overflow-y-auto">
              {deliverable.preview}
              {deliverable.preview.length >= 500 && '...'}
            </div>
          )}
          {deliverable.has_content && !deliverable.preview && deliverable.type === 'image' && (
            <div className="text-sm text-muted-foreground italic">
              Image generated - view in Work Queue
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

export default DemoModeTab;
